import { useState, useLayoutEffect, RefObject } from 'react';

import getContentBoxSize, { Bounds } from '../getContentBoxSize';

function updateBoundsIfChanged(
  prev: Bounds,
  newWidth: number,
  newHeight: number,
): Bounds {
  return prev.width === newWidth && prev.height === newHeight
    ? prev
    : { width: newWidth, height: newHeight };
}

function applyBoundsUpdate(width: number, height: number) {
  return (prev: Bounds) => updateBoundsIfChanged(prev, width, height);
}

/**
 * Gets the measure of an element using `ResizeObserver`
 * @param ref Element to be measured
 * @returns an object containing `width` and `height` for the element passed as ref (without padding)
 * @see AnimateHeight Tabs WorkflowBar
 */
export default function useMeasure(ref: RefObject<Element | null>) {
  const [bounds, setBounds] = useState<Bounds>({
    width: 0,
    height: 0,
  });

  useLayoutEffect(() => {
    const targetElement = ref?.current;
    if (!targetElement) return undefined;

    let rafId: number | null = null;
    const initialBounds = getContentBoxSize(targetElement);
    setBounds((prev) =>
      updateBoundsIfChanged(prev, initialBounds.width, initialBounds.height),
    );

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(([entry]: ResizeObserverEntry[]) => {
      if (!entry) return;
      const { width, height } = getContentBoxSize(entry.target, entry);
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() =>
        setBounds(applyBoundsUpdate(width, height)),
      );
    });

    observer.observe(targetElement);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ref.current used to re-run when ref is attached
  }, [ref?.current]);

  return bounds;
}
